import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MainLayout } from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { romanianLabels } from '@/utils/romanianLabels';
import { formatUserLevel } from '@/utils/formatters';
import {
  Users,
  Heart,
  Activity,
  FileText,
  TrendingUp,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Plus,
  BarChart,
  Baby,
  Loader2
} from 'lucide-react';
import { statisticsService, DashboardStatisticsResponse } from '@/services/statisticsService';
import { useToast } from '@/hooks/use-toast';

export const Dashboard: React.FC = () => {
  const { utilizator } = useAuth();
  const { toast } = useToast();
  const [statsData, setStatsData] = useState<DashboardStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await statisticsService.getDashboardStatistics();
        setStatsData(data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        toast({
          variant: 'destructive',
          title: 'Eroare',
          description: 'Nu s-au putut încărca statisticile.'
        });
      } finally {
        setLoading(false);
      }
    };

    if (utilizator) {
      fetchStats();
    }
  }, [utilizator, toast]);

  if (!utilizator) {
    return null;
  }

  const stats = [
    {
      title: 'Pacienți total',
      value: statsData?.patients.total.toString() || '0',
      change: statsData ? `+${statsData.patients.this_year}` : '+0',
      changeLabel: 'anul acesta',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'MCC Activ',
      value: statsData?.patients.active_chd_cases.toString() || '0',
      change: statsData ? `${((statsData.patients.active_chd_cases / (statsData.patients.total || 1)) * 100).toFixed(1)}%` : '0%',
      changeLabel: 'din total',
      icon: Activity,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Copii prematuri',
      value: statsData?.patients.premature_cases.toString() || '0',
      change: statsData ? `${((statsData.patients.premature_cases / (statsData.patients.total || 1)) * 100).toFixed(1)}%` : '0%',
      changeLabel: 'din total',
      icon: Baby,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Diagnostice MCC',
      value: statsData?.diagnoses.total_diagnoses.toString() || '0',
      change: statsData ? `+${statsData.diagnoses.unique_icd10_codes}` : '+0',
      changeLabel: 'coduri unice',
      icon: Heart,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Proceduri medicale',
      value: statsData?.procedures.total.toString() || '0',
      change: statsData ? `+${statsData.procedures.this_year}` : '+0',
      changeLabel: 'anul acesta',
      icon: Activity,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'info',
      message: 'Sistemul a fost inițializat cu succes',
      time: 'Acum',
      icon: CheckCircle,
    },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {romanianLabels.navigation.dashboard}
            </h1>
            <p className="text-muted-foreground">
              Bine ați venit înapoi, {utilizator.name || utilizator.username || 'utilizator'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {utilizator.hospital_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {formatUserLevel(utilizator.user_level?.id || utilizator.user_level_id)}
            </Badge>
            <Button variant="outline" size="sm">
              <Calendar className="mr-2 h-4 w-4" />
              Astăzi
            </Button>
            <Button size="sm">
              <FileText className="mr-2 h-4 w-4" />
              Generează raport
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <div className={`rounded-full p-2 ${stat.bgColor}`}>
                    {loading ? (
                      <Loader2 className={`h-4 w-4 animate-spin ${stat.color}`} />
                    ) : (
                      <Icon className={`h-4 w-4 ${stat.color}`} />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <p className="text-xs text-muted-foreground">
                        <span className="text-green-600">{stat.change}</span> {stat.changeLabel}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Prezentare generală</TabsTrigger>
            <TabsTrigger value="activity">Activitate recentă</TabsTrigger>
            <TabsTrigger value="reports">Rapoarte</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Acțiuni rapide</CardTitle>
                  <CardDescription>
                    Accesați rapid funcțiile principale ale sistemului
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-2">
                  <Button variant="outline" className="justify-start">
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă pacient nou
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <Heart className="mr-2 h-4 w-4" />
                    Înregistrează diagnostic
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <Activity className="mr-2 h-4 w-4" />
                    Înregistrează procedură
                  </Button>
                  <Button variant="outline" className="justify-start">
                    <BarChart className="mr-2 h-4 w-4" />
                    Vizualizează statistici
                  </Button>
                </CardContent>
              </Card>

              {/* System Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Status sistem</CardTitle>
                  <CardDescription>
                    Monitorizare în timp real a sistemului
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Bază de Date</span>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Operațional
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">API Backend</span>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Operațional
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Autentificare JWT</span>
                    </div>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Activ
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm">Sincronizare EUROCAT</span>
                    </div>
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      În așteptare
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Informații utilizator</CardTitle>
                <CardDescription>
                  Detaliile contului dumneavoastră
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Nume</p>
                    <p className="text-sm font-semibold">{utilizator.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm font-semibold">{utilizator.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Nivel acces</p>
                    <p className="text-sm font-semibold">{formatUserLevel(utilizator.user_level?.id || utilizator.user_level_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Spital ID</p>
                    <p className="text-sm font-semibold">{utilizator.hospital_id || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Activitate recentă</CardTitle>
                <CardDescription>
                  Ultimele acțiuni efectuate în sistem
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivities.map((activity) => {
                    const Icon = activity.icon;
                    return (
                      <div key={activity.id} className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm">{activity.message}</p>
                          <p className="text-xs text-muted-foreground">
                            <Clock className="mr-1 inline h-3 w-3" />
                            {activity.time}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {recentActivities.length === 0 && (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground">
                        Nu există activitate recentă de afișat.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Rapoarte disponibile</CardTitle>
                <CardDescription>
                  Generați și descărcați rapoarte pentru analiza datelor
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" disabled>
                  <FileText className="mr-2 h-4 w-4" />
                  Raport lunar pacienți
                </Button>
                <Button variant="outline" className="w-full justify-start" disabled>
                  <BarChart className="mr-2 h-4 w-4" />
                  Statistici diagnostice MCC
                </Button>
                <Button variant="outline" className="w-full justify-start" disabled>
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Analiză proceduri medicale
                </Button>
                <div className="pt-3 text-sm text-muted-foreground">
                  Rapoartele vor fi disponibile după ce vor fi înregistrate date în sistem.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};